import { supabase } from '@/integrations/supabase/client';
import { errorLogger } from '@/lib/error-logger';
import { PRICING_CONFIG } from '@/config/pricing';
import type { Database } from '@/integrations/supabase/types';

type Subscription = Database['public']['Tables']['subscriptions']['Row'];
type UsageLog = Database['public']['Tables']['usage_logs']['Row'];
type Json = Database['public']['Tables']['usage_logs']['Insert']['metadata'];

export interface UsageStats {
  posts: number;
  connectedAccounts: number;
  teamMembers: number;
  aiImages: number;
  contentDnaAnalyses: number;
}

export interface LimitCheckResult {
  allowed: boolean;
  reason?: string;
  currentUsage?: number;
  limit?: number | string;
}

/**
 * Usage Tracking and Limits Enforcement Service
 * Updated for monthly-only, per-seat pricing model
 */
export class UsageService {
  /**
   * Get current usage statistics for an organization
   */
  static async getUsageStats(organizationId: string): Promise<UsageStats> {
    const now = new Date();
    const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);

    // Get usage logs for current billing period
    const { data: usageLogs, error } = await supabase
      .from('usage_logs')
      .select('feature, quantity')
      .eq('organization_id', organizationId)
      .gte('created_at', startOfMonth.toISOString());

    if (error) {
      await errorLogger.log(error, { severity: 'error', additionalContext: { organizationId } });
      return { posts: 0, connectedAccounts: 0, teamMembers: 0, aiImages: 0, contentDnaAnalyses: 0 };
    }

    // Aggregate usage by feature
    const stats: UsageStats = {
      posts: 0,
      connectedAccounts: 0,
      teamMembers: 0,
      aiImages: 0,
      contentDnaAnalyses: 0,
    };

    usageLogs?.forEach((log) => {
      switch (log.feature) {
        case 'post':
          stats.posts += log.quantity;
          break;
        case 'connected_account':
          stats.connectedAccounts += log.quantity;
          break;
        case 'team_member':
          stats.teamMembers += log.quantity;
          break;
        case 'ai_image':
          stats.aiImages += log.quantity;
          break;
        case 'content_dna':
          stats.contentDnaAnalyses += log.quantity;
          break;
      }
    });

    return stats;
  }

  /**
   * Get subscription tier for an organization
   */
  static async getSubscriptionTier(organizationId: string): Promise<string> {
    const { data: subscription } = await supabase
      .from('subscriptions')
      .select('tier, status')
      .eq('organization_id', organizationId)
      .maybeSingle();

    // Default to starter if no subscription or inactive
    if (!subscription || subscription.status !== 'active') {
      return 'starter';
    }

    return subscription.tier || 'starter';
  }

  /**
   * Get limits for a specific tier
   */
  static getLimitsForTier(tier: string) {
    const tierConfig = PRICING_CONFIG[tier as keyof typeof PRICING_CONFIG];
    if (!tierConfig) {
      return PRICING_CONFIG.starter.limits;
    }
    return tierConfig.limits;
  }

  /**
   * Check if organization can create a post
   * Note: Core, Team, Pro have unlimited posts
   */
  static async canCreatePost(organizationId: string): Promise<LimitCheckResult> {
    const tier = await this.getSubscriptionTier(organizationId);
    const limits = this.getLimitsForTier(tier);
    
    // Check for unlimited
    if (limits.postsPerMonth === 'Unlimited') {
      return { allowed: true };
    }

    const usage = await this.getUsageStats(organizationId);
    const limit = Number(limits.postsPerMonth);
    const allowed = usage.posts < limit;

    return {
      allowed,
      reason: allowed ? undefined : `You've reached your monthly limit of ${limit} posts. Upgrade to post unlimited.`,
      currentUsage: usage.posts,
      limit: limits.postsPerMonth,
    };
  }

  /**
   * Check if organization can connect a social account
   * Note: Core, Team, Pro have unlimited accounts
   */
  static async canConnectAccount(organizationId: string): Promise<LimitCheckResult> {
    const tier = await this.getSubscriptionTier(organizationId);
    const limits = this.getLimitsForTier(tier);

    // Get current connected accounts count
    const { count } = await supabase
      .from('social_accounts')
      .select('*', { count: 'exact', head: true })
      .eq('organization_id', organizationId)
      .eq('is_active', true);

    const currentCount = count || 0;

    // Check for unlimited
    if (limits.connectedAccounts === 'Unlimited') {
      return { allowed: true, currentUsage: currentCount };
    }

    const limit = Number(limits.connectedAccounts);
    const allowed = currentCount < limit;

    return {
      allowed,
      reason: allowed ? undefined : `You've reached your limit of ${limit} connected accounts. Upgrade for unlimited.`,
      currentUsage: currentCount,
      limit: limits.connectedAccounts,
    };
  }

  /**
   * Check if organization can invite a team member
   */
  static async canInviteTeamMember(organizationId: string): Promise<LimitCheckResult> {
    const tier = await this.getSubscriptionTier(organizationId);
    const limits = this.getLimitsForTier(tier);

    // Get current team members count
    const { count } = await supabase
      .from('user_roles')
      .select('*', { count: 'exact', head: true })
      .eq('organization_id', organizationId);

    const currentCount = count || 0;

    // Check for unlimited or special format like "10+"
    if (typeof limits.teamMembers === 'string' && 
        (limits.teamMembers === 'Unlimited' || limits.teamMembers.includes('+'))) {
      return { allowed: true, currentUsage: currentCount };
    }

    const limit = Number(limits.teamMembers);
    const allowed = currentCount < limit;

    return {
      allowed,
      reason: allowed ? undefined : `You've reached your limit of ${limit} team members. Upgrade for more seats.`,
      currentUsage: currentCount,
      limit: limits.teamMembers,
    };
  }

  /**
   * Check if organization can generate an AI image
   * Note: Core, Team, Pro have unlimited images
   */
  static async canGenerateAIImage(organizationId: string): Promise<LimitCheckResult> {
    const tier = await this.getSubscriptionTier(organizationId);
    const limits = this.getLimitsForTier(tier);
    
    // Check for unlimited
    if (limits.aiImages === 'Unlimited') {
      return { allowed: true };
    }
    
    const usage = await this.getUsageStats(organizationId);
    const limit = Number(limits.aiImages);
    const allowed = usage.aiImages < limit;

    return {
      allowed,
      reason: allowed ? undefined : `You've reached your monthly limit of ${limit} AI images. Upgrade for unlimited.`,
      currentUsage: usage.aiImages,
      limit: limits.aiImages,
    };
  }

  /**
   * Check if organization can analyze content DNA
   */
  static async canAnalyzeContentDNA(organizationId: string): Promise<LimitCheckResult> {
    const tier = await this.getSubscriptionTier(organizationId);
    const limits = this.getLimitsForTier(tier);
    const usage = await this.getUsageStats(organizationId);

    // Starter tier has limited DNA access
    if (!limits.contentDNA || limits.contentDNA.sources === 0) {
      return {
        allowed: false,
        reason: 'ContentDNA is not available on your plan. Upgrade to Core or higher.',
        currentUsage: usage.contentDnaAnalyses,
        limit: 0,
      };
    }

    // Check for unlimited
    if (limits.contentDNA.sources === 'Unlimited') {
      return { allowed: true, currentUsage: usage.contentDnaAnalyses };
    }

    const limit = Number(limits.contentDNA.sources);
    const allowed = usage.contentDnaAnalyses < limit;

    return {
      allowed,
      reason: allowed ? undefined : `You've reached your limit of ${limit} DNA analyses. Upgrade for unlimited.`,
      currentUsage: usage.contentDnaAnalyses,
      limit: limits.contentDNA.sources,
    };
  }

  /**
   * Track usage for a feature
   */
  static async trackUsage(
    organizationId: string,
    feature: 'post' | 'connected_account' | 'team_member' | 'ai_image' | 'content_dna',
    quantity: number = 1,
    metadata?: Record<string, unknown>
  ): Promise<void> {
    try {
      const { error } = await supabase
        .from('usage_logs')
        .insert({
          organization_id: organizationId,
          feature,
          quantity,
          metadata: (metadata || null) as Json,
        });
      
      if (error) {
        throw error;
      }
    } catch (error) {
      await errorLogger.log(error as Error, { 
        severity: 'warning', 
        additionalContext: { organizationId, feature, quantity } 
      });
    }
  }

  /**
   * Get usage percentage for a specific feature
   */
  static calculateUsagePercentage(current: number, limit: number | string): number {
    if (limit === 'Unlimited' || typeof limit === 'string') {
      return 0;
    }
    return Math.min(Math.round((current / limit) * 100), 100);
  }

  /**
   * Get warning level based on usage percentage
   */
  static getWarningLevel(percentage: number): 'safe' | 'warning' | 'danger' | 'exceeded' {
    if (percentage >= 100) return 'exceeded';
    if (percentage >= 90) return 'danger';
    if (percentage >= 80) return 'warning';
    return 'safe';
  }

  /**
   * Get seat usage for an organization
   */
  static async getSeatUsage(organizationId: string): Promise<{ used: number; limit: number | string }> {
    const { data: subscription } = await supabase
      .from('subscriptions')
      .select('seats_used, seats_limit')
      .eq('organization_id', organizationId)
      .maybeSingle();

    return {
      used: subscription?.seats_used || 0,
      limit: subscription?.seats_limit || 1,
    };
  }

  /**
   * Check if paywall should be shown for a feature
   */
  static async checkPaywallNeeded(
    organizationId: string,
    feature: 'post' | 'connected_account' | 'team_member' | 'ai_image' | 'content_dna'
  ): Promise<LimitCheckResult> {
    switch (feature) {
      case 'post':
        return this.canCreatePost(organizationId);
      case 'connected_account':
        return this.canConnectAccount(organizationId);
      case 'team_member':
        return this.canInviteTeamMember(organizationId);
      case 'ai_image':
        return this.canGenerateAIImage(organizationId);
      case 'content_dna':
        return this.canAnalyzeContentDNA(organizationId);
      default:
        return { allowed: false, reason: 'Unknown feature' };
    }
  }
}
